// Domain constants for the invoice-processing workflow.
//
// THE WIRE VOCABULARY IS IMPORTED, NOT COPIED (code review D-08, 2026-10-03).
// Every stored value, enum, flag copy and shared limit below comes from
// `@ap/shared` — the same module the server validates against. What this file
// adds is the app's own layer: its `Role` names, screen labels, the PascalCase
// key names the screens were written against, and money in dollars where the
// app keeps money as a JS number (`api/types.ts`'s PORT DECISION).

import {
  ANALYTICS_ASSIGNMENT_ROLES as SHARED_ANALYTICS_ASSIGNMENT_ROLES,
  APPROVAL_ASSIGNMENT_ROLES as SHARED_APPROVAL_ASSIGNMENT_ROLES,
  APPROVAL_TIER_STEPS as SHARED_APPROVAL_TIER_STEPS,
  ASSIGNMENT_ROLE,
  ASSIGNMENT_ROLE_LABELS,
  CAPITAL_EQUIPMENT_MINIMUM_CENTS,
  DECISION_OUTCOME,
  DECISION_STEP,
  FLAG_TYPES as SHARED_FLAG_TYPES,
  INVOICE_STATUS,
  REASON_CATEGORIES_FOR_OUTCOME,
  REASON_CATEGORY_LABELS,
  REASON_REQUIRED_OUTCOMES,
  USER_ROLES,
  RETIRED_DECISION_STEP,
  RETIRED_DECISION_STEP_LABELS,
  WORKSPACE_KIND,
  type AnalyticsSection,
  type DecisionOutcome as SharedDecisionOutcome,
  type DecisionStep as SharedDecisionStep,
  type FlagResolution,
  type InvoiceStatus as SharedInvoiceStatus,
  type ReasonCategory,
  type ReferenceSection,
  type RulesSection,
  type UserRole,
  type WorkspaceKind,
} from '@ap/shared'
import type { AssignmentScreen } from './identity'

export {
  ANALYTICS_LENS_HEADER,
  ANALYTICS_SECTION_ASSIGNMENT_ROLE,
  ANALYTICS_SECTION_LABEL,
  ANALYTICS_SECTIONS,
  ASSIGNMENT_ACTION,
  ASSIGNMENT_COMPANY_SCOPE,
  CAPITAL_EQUIPMENT_DEPARTMENT_ID,
  FLEET_COMPANY_ID,
  FLEET_COMPANY_NAME,
  HOLD_CATEGORIES,
  MAX_IDS_PER_REQUEST,
  REFERENCE_SECTION_LABEL,
  REFERENCE_SECTION_ROLE_LABEL,
  REFERENCE_SECTIONS,
  RETIRED_DECISION_STEP,
  RULES_SECTION_LABEL,
  RULES_SECTIONS,
  type AnalyticsSection,
  type AssignmentCompanyScope,
  type AssignmentRole,
  type FlagResolution,
  type ReasonCategory,
  type RecordedDecisionStep,
  type ReferenceSection,
  type RulesSection,
} from '@ap/shared'

// ---- Roles -----------------------------------------------------------------
// UX-routing roles only. Real access is enforced by Dataverse RLS + the broker flow.
// Three-tier linear approval (planning §3): Department → Director → Oversight
// replace the old Primary/Secondary pair. DirectorApproval keeps the
// owner-team semantics the old PrimaryApproval had (the company team that owns
// the invoice); DepartmentApproval is a new per-company tier that owns nothing;
// OversightApproval is the renamed internal Secondary.
export type Role =
  | 'ImportControl'
  // Controller Approval (owner, 2026-10-07 — D-303): the alternative to
  // Manager Approval for an invoice a Controller routing rule matches. A
  // company-scoped `controller` appointment, like the Director and Manager seats.
  | 'ControllerApproval'
  | 'DepartmentApproval'
  | 'DirectorApproval'
  | 'OversightApproval'
  | 'ExportControl'
  | 'ClosingConfirmation'
  | 'DenialReview'
  | 'Archives'
  | 'Admin'
  // The References and Rules BLOCKS, by name — screens, not roles: no
  // `user_role` token maps to either (D-293). The Assignments screen and the
  // subheaders key the two blocks by them.
  | 'References'
  | 'Rules'
  // PER-TAB roles inside the References and Rules blocks (owner item 29,
  // 2026-08-28) — the only grant for either screen since D-275. One per tab,
  // so a person can be given the Vehicles master without the vendor list. NONE
  // is cross-company: holding one does not make the server's `isInternal`
  // true (`backend/src/domain/authority.ts`).
  | 'ReferencesVendors'
  | 'ReferencesCompanies'
  | 'ReferencesCodes'
  | 'ReferencesVehicles'
  | 'ReferencesMarketing'
  | 'RulesVendors'
  | 'RulesCompanies'
  | 'RulesCodes'
  | 'RulesGuidance'
  // The fifth Rules tab (owner, 2026-09-15): the criteria that forward an
  // invoice for Oversight Approval. Same contract as the four above.
  | 'RulesOversight'
  // Rules -> Grouping (owner, 2026-10-22 — D-402).
  | 'RulesGrouping'
  // The Analytics tab's two tabs. Since 2026-10-01 (D-258) each is lit by an
  // `analytics-dashboard` / `analytics-reporting` APPOINTMENT for one company,
  // not by a user role — `sessionFromProfile` derives them from the seats.
  | 'AnalyticsDashboard'
  | 'AnalyticsReporting'
  // The Analytics BLOCK, by name — a screen, not a role, like the two above.
  | 'Analytics'

// ---- Workspaces -------------------------------------------------------------
// A workspace is a class of document flowing through the pipeline. Invoices is
// the only one at launch; the level is kept (state, label map, per-workspace
// role projection) so adding a second one is a data change rather than a
// refactor. Admin / References / Rules / Analytics are workspace-independent.
export type Workspace = WorkspaceKind
export const WORKSPACES: readonly Workspace[] = Object.values(WORKSPACE_KIND)
export const WORKSPACE_LABEL: Record<Workspace, string> = { invoice: 'Invoices' }
/** The sidebar's group heading for each workspace (owner, 2026-09-28). Claims
    Processing and Payroll are expected as the next two headings; each arrives
    with its workspace, not before. */
export const WORKSPACE_NAV_HEADING: Record<Workspace, string> = { invoice: 'Invoice Processing' }

// ---- Invoice status enum (invoices.status) ------------------
export const InvoiceStatus = {
  PendingMatching: INVOICE_STATUS.PendingMatching,
  // 'Matching Failed' and 'Pending AP/AR Approval Decision' are RETIRED (question
  // 4.11; D-215, D-222) — see `shared/src/enums.ts`'s header.
  PendingImportControl: INVOICE_STATUS.PendingImportControlDecision,
  PendingController: INVOICE_STATUS.PendingControllerApprovalDecision,
  PendingDepartment: INVOICE_STATUS.PendingDepartmentApprovalDecision,
  PendingDirector: INVOICE_STATUS.PendingDirectorApprovalDecision,
  PendingOversight: INVOICE_STATUS.PendingOversightApprovalDecision,
  PendingExportControl: INVOICE_STATUS.PendingExportControlDecision,
  PendingDenialReview: INVOICE_STATUS.PendingDenialReviewDecision,
  PendingExport: INVOICE_STATUS.PendingExport,
  Imported: INVOICE_STATUS.ImportedIntoPointClickCare,
  Typed: INVOICE_STATUS.TypedIntoPointClickCare,
  PaymentDenied: INVOICE_STATUS.PaymentDenied,
  RevisedInvoiceRequested: INVOICE_STATUS.RevisedInvoiceRequested,
  Other: INVOICE_STATUS.Other,
  Blank: INVOICE_STATUS.Blank,
  DifferentTypeOfDocument: INVOICE_STATUS.DifferentTypeOfDocument,
  Duplicate: INVOICE_STATUS.Duplicate,
} as const satisfies Record<string, SharedInvoiceStatus>
export type InvoiceStatus = SharedInvoiceStatus

// `statusLabel`, which turns a STORED `invoices.status` into what a user reads,
// sits after `TIMELINE_NODES` below, because it is built from them (D-449).

// ---- Decision step + outcome enums (decisions) -------------------------
export const DecisionStep = {
  ImportControl: DECISION_STEP.ImportControl,
  // 'AP/AR Approval Decision' is RETIRED (D-215, D-223): `RETIRED_DECISION_STEP`
  // names it so History can label old rows.
  Controller: DECISION_STEP.ControllerApproval,
  Department: DECISION_STEP.DepartmentApproval,
  Director: DECISION_STEP.DirectorApproval,
  Oversight: DECISION_STEP.OversightApproval,
  ExportControl: DECISION_STEP.ExportControl,
  ClosingConfirmation: DECISION_STEP.ClosingConfirmation,
  DenialReview: DECISION_STEP.DenialReview,
} as const satisfies Record<string, SharedDecisionStep>
export type DecisionStep = SharedDecisionStep

/**
 * The one place a STORED `decisions.step` (or `TIMELINE_NODES`/config step)
 * value is translated to what a user reads (owner item 8, 2026-08-19,
 * `docs/decisions.md` D-43 — "Department Approval" → "Manager Approval").
 * History's `historyStepLabel` (`./history.ts`) must read through this, with its old
 * `step.replace(/ Decision$/, '')` transform kept only as the fallback for a
 * step this map does not name — otherwise Decision History (History's
 * predecessor, D-329) kept printing
 * "Department Approval" after the timeline and every screen title had moved
 * on. `TIMELINE_NODES` below and `App.tsx`'s per-tier `config.title` both read
 * their label from here too, so the three can never drift apart again. */
export const STEP_LABEL: Record<string, string> = {
  [DecisionStep.ImportControl]: 'Import Control',
  // RETIRED (D-223): an old decision, hold or correction row still reads
  // "AP/AR Approval" in history, as `RETIRED_DECISION_STEP_LABELS` says.
  [RETIRED_DECISION_STEP.ApArApproval]: RETIRED_DECISION_STEP_LABELS[RETIRED_DECISION_STEP.ApArApproval],
  [DecisionStep.Controller]: 'Controller Approval',
  [DecisionStep.Department]: 'Manager Approval',
  [DecisionStep.Director]: 'Director Approval',
  [DecisionStep.Oversight]: 'Oversight Approval',
  [DecisionStep.ExportControl]: 'Export Control',
  // RETIRED (D-311): an old Manual Entry decision still reads "Manual Entry".
  [RETIRED_DECISION_STEP.ManualEntry]: RETIRED_DECISION_STEP_LABELS[RETIRED_DECISION_STEP.ManualEntry],
  [DecisionStep.ClosingConfirmation]: 'Closing Confirmation',
  [DecisionStep.DenialReview]: 'Denial Review',
}

// `Escalated` (question 4.10) was machine-written only — the escalation worker
// wrote it on its hourly tick until D-336 removed the worker. There is no UI
// control anywhere that submits it; the rows it wrote render in History.
export const DecisionOutcome = {
  Approved: DECISION_OUTCOME.approved,
  Denied: DECISION_OUTCOME.denied,
  ArchivedWithoutPayment: DECISION_OUTCOME.archivedWithoutPayment,
  ArchivedForFreshInvoice: DECISION_OUTCOME.archivedForFreshInvoice,
  Archived: DECISION_OUTCOME.archived,
  // EXPORT CONTROL ONLY: Typed Into Point Click Care (D-311).
  RequiresManualEntry: DECISION_OUTCOME.requiresManualEntry,
  Escalated: DECISION_OUTCOME.escalated,
  // DENIAL REVIEW ONLY (owner, 2026-09-25): on to the first qualifying
  // approval step (D-324); back to Import Control from D-312 until then.
  Reinstated: DECISION_OUTCOME.reinstated,
} as const satisfies Record<string, SharedDecisionOutcome>
export type DecisionOutcome = SharedDecisionOutcome

// Outcomes that require a reason comment before they commit.
export const REASON_REQUIRED: ReadonlySet<string> = new Set(REASON_REQUIRED_OUTCOMES)

/** Item 8's floor in DOLLARS: the app keeps money as a JS number, so this is
    `CAPITAL_EQUIPMENT_MINIMUM_CENTS` / 100. The SERVER is the gate — this only
    stops the picker offering a pairing the server will refuse. */
const CAPITAL_EQUIPMENT_MINIMUM = CAPITAL_EQUIPMENT_MINIMUM_CENTS / 100

/** Money here is a plain number (dollars), not cents — see the PORT DECISION at the top of `api/types.ts`. */
export function capitalEquipmentAllowed(total: number | null | undefined): boolean {
  return total !== null && total !== undefined && Math.abs(total) >= CAPITAL_EQUIPMENT_MINIMUM
}

// ---- Assignments-based authorization ---------------------------------------
// The four approval tiers (Controller / Department / Director / Oversight) authorize from an
// active appointment for the record's company — the appointment row IS the
// permission. These are the steps where the GL code is editable but nothing
// else is; the API decides who may, by step, and writes the correction row
// itself (api-contract §4.6).
export const APPROVAL_TIER_STEPS: ReadonlySet<string> = new Set(SHARED_APPROVAL_TIER_STEPS)

// assignments.role — who may act at a tier. Every row but 'oversight' names a
// company (company_id/name).
// 'controller', 'director' and 'department-head' are company-scoped: one seat, one company.
// 'oversight' is NOT, and has not been since D-124 (2026-09-15) — its seat is
// org-wide and names no company at all, which since D-134 (2026-09-22) is
// company_scope = 'organization' rather than a company called 'All'.
export const ASSIGNMENT_ROLES = ASSIGNMENT_ROLE

/** Every assignment role's label. `'department-head'` displays as **Manager
    Approval** (D-43). */
export const ASSIGNMENT_ROLE_LABEL: Record<string, string> = { ...ASSIGNMENT_ROLE_LABELS }

/**
 * WHERE A ROLE SITS IN THE PIPELINE, 1-based, for the Assignments screen's
 * Invoices tab (owner, 2026-09-14: "We do not formally number the steps
 * elsewhere, but I want to easily be able to read the pipeline from start to
 * finish, and alphabetizing does not cover that").
 *
 * DERIVED FROM `TIMELINE_NODES`, never a second hand-written order — that array
 * is the pipeline as the app draws it across the top of the Invoices tab, so a
 * step inserted there is numbered here for free instead of silently keeping the
 * old order. The number is this screen's own reading aid and is not a stored
 * value or a name anything else uses.
 *
 * `undefined` for a role with no node, such as a References tab role: it
 * decides nothing and sits in no queue, so it has no place in the sequence.
 */
export function pipelineStepNumber(role: Role): number | undefined {
  const at = TIMELINE_NODES.findIndex((n) => n.role === role)
  return at === -1 ? undefined : at + 1
}

/** The same, for the approval tiers, which arrive as `assignment_role` tokens
    rather than `Role` values. */
export const ASSIGNMENT_ROLE_TO_ROLE: Record<string, Role> = {
  oversight: 'OversightApproval',
  director: 'DirectorApproval',
  'department-head': 'DepartmentApproval',
  controller: 'ControllerApproval',
}

/** The four approval tiers' seats. */
export const APPROVAL_ASSIGNMENT_ROLES: readonly string[] = SHARED_APPROVAL_ASSIGNMENT_ROLES

/** The seats of the default path (owner, 2026-10-09 — D-336): Manager and
    Director Approval, in pipeline order, which every invoice requires unless a
    routing rule says otherwise. The Assignments screen's missing-approver
    warning keys on these, and a company missing EITHER one is warned about,
    because every required step waits when nobody holds it (D-336, amended
    2026-10-09). Controller Approval is required only by a rule, so a
    Controller seat stands in for neither, and the org-wide Oversight seat
    names no company. */
export const DEFAULT_PATH_APPROVAL_ROLES: readonly string[] = [ASSIGNMENT_ROLE.departmentHead, ASSIGNMENT_ROLE.director]

export function assignmentRoleLabel(role: string | undefined | null): string {
  if (!role) return ''
  return ASSIGNMENT_ROLE_LABEL[role] ?? role
}


// ---- Decision reason categories (decisions.reason_category) ----
//
// REQUIRED, NOT OPTIONAL, ON THE THREE OUTCOMES A PERSON PICKS FROM A DIALOG
// (owner, 2026-09-15: "The Category picker for denials should not be optional
// ... Add the same mandatory Category pickers to the pop-ups for Not An Invoice
// (Blank, Statement, Other) and Move To Manual Entry (Split Between Companies,
// Other). In all three cases, make the Reason required only if Other is chosen
// as the category.").
//
// It was a coarse optional tag beside a required free-text reason, which had it
// exactly backwards: the countable field was the one nobody had to fill in.
//
// The stored value is the stable left-hand key; the label is the dropdown
// display, TITLE CASE on every word (owner, same day — these are the names of
// fixed categories, not sentence fragments). Both come from `@ap/shared`.
export const REASON_CATEGORIES: Readonly<Record<ReasonCategory, string>> = REASON_CATEGORY_LABELS

export function reasonCategoryLabel(key: string | undefined | null): string {
  if (!key) return ''
  return (REASON_CATEGORIES as Record<string, string>)[key] ?? key
}

/** The key every list ends on, and the only one that turns the free-text reason
    back on: picking it says "none of these", which is exactly when prose is the
    only explanation left. */
export const REASON_CATEGORY_OTHER = 'other'

/** Deny — the four denial reasons, plus Other. */
export const DENY_CATEGORIES: readonly ReasonCategory[] =
  REASON_CATEGORIES_FOR_OUTCOME[DECISION_OUTCOME.denied] ?? []

/** The Archive dialog (owner, 2026-09-15). The button that opens it was called
    *Not An Invoice* until the same day and is now **Archive**, so those words
    name one of its answers rather than the action. */
export const ARCHIVE_CATEGORIES: readonly ReasonCategory[] =
  REASON_CATEGORIES_FOR_OUTCOME[DECISION_OUTCOME.archived] ?? []

/** Move To Manual Entry (owner, 2026-09-15). */
export const MANUAL_ENTRY_CATEGORIES: readonly ReasonCategory[] =
  REASON_CATEGORIES_FOR_OUTCOME[DECISION_OUTCOME.requiresManualEntry] ?? []

/** The warning every archive confirmation shows, word for word (owner items 3
    and 11, 2026-08-28). ONE constant, two dialogs — the Archive confirmation
    (`DecisionDialog`) and the duplicate flag's "Archive invoice" confirmation in
    `EditableInvoiceForm` — because the owner gave the same sentence for both
    and two copies of a sentence drift. */
export const ARCHIVE_WARNING =
  'Archiving permanently removes this document from the approval pipeline. It will never be paid, and this action cannot be undone.'

// ---- Flag types + resolution policy ----------------------------------------
// Every flag clears by its own "Mark Resolved" click (owner, 2026-09-25 —
// D-149). An `auto` flag's button is disabled until its underlying condition
// holds; an `explicit` flag's is always enabled. Nothing clears on save.
/** Keyed by stored value. The copy is `shared/src/constants.ts`'s, untouched. */
export const FLAG_TYPES: Record<string, { label: string; resolution: FlagResolution; description: string }> =
  Object.fromEntries(
    SHARED_FLAG_TYPES.map((f) => [f.flag_type, { label: f.label, resolution: f.resolution, description: f.description }]),
  )

/**
 * A flag's label. A RETIRED flag (`GLCodeLineMismatch`, D-401, and the three
 * before it) is in no live copy, but a stored flag still carries its label
 * joined from its `flag_definitions` seed row: pass it as `stored`, so an old
 * flag reads by its label, never its stored value.
 */
export function flagLabel(flag: string | undefined, stored?: string | null): string {
  if (!flag) return 'Flag'
  return FLAG_TYPES[flag]?.label ?? (stored || flag)
}

export function flagResolution(flag: string | undefined): FlagResolution {
  if (!flag) return 'explicit'
  return FLAG_TYPES[flag]?.resolution ?? 'explicit'
}

export function flagDescription(flag: string | undefined, stored?: string | null): string {
  if (!flag) return ''
  return FLAG_TYPES[flag]?.description ?? stored ?? ''
}

/**
 * The flag types matching raises PER LINE ITEM, each carrying that line's
 * number as its `detail` (`backend/src/domain/matchingLadder.ts` writes
 * `String(line_number)`, and `<line>: <codes>` for `UnrecognizedGLCode`). The
 * invoice modal names the line in front of these rows. Read off each flag's
 * shared `level`.
 */
export const LINE_SCOPED_FLAG_TYPES: ReadonlySet<string> = new Set(
  SHARED_FLAG_TYPES.filter((f) => f.level === 'line_item').map((f) => f.flag_type),
)

/**
 * The leading line sequence in a flag's `detail`, or null when there isn't one.
 *
 * Four flag types are raised PER LINE ITEM and carry that line's sequence as
 * their whole detail (`docs/flags.md`, "Exactly when each flag is raised"):
 * `LineItemExtractionLowConfidence`, `MissingAccount`,
 * `AccountMatchLowConfidence`, and `UnrecognizedGLCode` — the last as
 * `<seq>: <codes>`, which is why this reads a LEADING integer rather than
 * parsing the whole string. Everything else returns null, either because the
 * detail is absent or because it is a uuid, a currency or a company name.
 */
export function flagDetailSequence(detail: string | null | undefined): number | null {
  if (!detail) return null
  // THE DIGITS MUST BE THE WHOLE DETAIL, OR BE FOLLOWED BY A COLON. A bare
  // `^\s*(\d+)` also matched a duplicate flag's `detail`, which is a uuid:
  // `11111111-2222-3333-4444-555555555555` read as sequence 11111111 and sorted
  // the row by an internal id. Caught by this file's own test, 2026-08-31.
  const match = /^\s*(\d+)\s*(?::|$)/.exec(detail)
  return match ? Number(match[1]) : null
}

/**
 * The collation key for a flag's label: the label with any leading punctuation
 * removed.
 *
 * `“Fuzzy” Duplicate Detected` opens with a curly quote, and `localeCompare`
 * sorted it above `Amounts Don't Reconcile` because of it — a reader scanning
 * an alphabetical list looks for that row under F, not at the top. Only the
 * LEADING run is stripped; the rest of the label collates normally, so the
 * apostrophe inside `Don't` still counts exactly as it did.
 */
function flagSortKey(flag: string | null | undefined, stored?: string | null): string {
  return flagLabel(flag ?? undefined, stored).replace(/^[^\p{L}\p{N}]+/u, '')
}

/**
 * The order flag rows read in, in the invoice modal (owner item 7, 2026-08-31:
 * *"I am not sure how flags in the invoice modal are currently sorted, but
 * alphabetically is probably the way to go."*).
 *
 * ALPHABETICAL BY THE LABEL, not by the stored `flag_type`. The two disagree
 * constantly — `LineItemsToTotalMismatch` reads "Amounts Don't Reconcile",
 * `InvoiceExtractionLowConfidence` reads "Low Invoice Confidence" — and the
 * label is the only one of the pair a reviewer can see, so sorting on the
 * stored value would produce an order that looks arbitrary on screen.
 *
 * TIES KEEP LINE ORDER. Four flag types repeat once per offending line item,
 * so several rows can share one label; `flagDetailSequence` puts those in line
 * order. Insertion order is NOT a safe fallback for them: the server orders by
 * `created_at, id` and the matching worker writes a whole invoice's flags in one
 * pass, so equal timestamps fall through to a uuid tiebreak. Rows with no
 * sequence at all sort after those that have one and otherwise keep their
 * incoming order, which `Array.prototype.sort`'s stability guarantees.
 *
 * PRESENTATION ONLY — nothing in `src/api/` or the mock sorts, because the
 * order flags are STORED in is the order matching raised them and that is a
 * record.
 */
export function compareFlagsForDisplay(
  a: { flag_type?: string | null; label?: string | null; detail?: string | null },
  b: { flag_type?: string | null; label?: string | null; detail?: string | null },
): number {
  // By the label the row reads by: a retired flag's comes from its stored row.
  const byLabel = flagSortKey(a.flag_type, a.label).localeCompare(flagSortKey(b.flag_type, b.label))
  if (byLabel !== 0) return byLabel
  const seqA = flagDetailSequence(a.detail)
  const seqB = flagDetailSequence(b.detail)
  if (seqA === null && seqB === null) return 0
  if (seqA === null) return 1
  if (seqB === null) return -1
  return seqA - seqB
}

/** What the Assignments screen's Assign dialog calls the org-wide Oversight
    target — a phrase, not a company name, because there is no company to name.
    The Company COLUMN is a different string: `rowsOf` writes `ALL_COMPANIES`
    (`'All'`) there for every centralized role, org-wide and internal alike. */
export const ORG_WIDE_COMPANY_LABEL = 'All companies'

// The Account and Department source labels are `@ap/shared`'s
// `ACCOUNT_SOURCE_LABELS` / `DEPARTMENT_SOURCE_LABELS`; nothing here copies them.

// ---- Toaster ----------------------------------------------------------------
// One app-wide Toaster (mounted in App.tsx); screens dispatch to it by id.
export const TOASTER_ID = 'invoice-processing-toaster'

// ---- Timeline nodes (the single-page step timeline) ------------------------
// Main-path order: Import Control → Department → Director → Oversight →
// Export Control → Closing Confirmation, with Controller Approval drawn directly
// above Department as the alternative between Import Control and Director
// (D-303), and Denial Review as a branch node.
export type NodeKey =
  | 'import-control'
  | 'controller'
  | 'department'
  | 'director'
  | 'oversight'
  | 'export-control'
  | 'closing-confirmation'
  | 'denial-review'
  | 'archives'

export interface TimelineNode {
  key: NodeKey
  label: string
  role: Role
  /** Statuses whose invoices appear in this node's queue(s). */
  queueStatuses: InvoiceStatus[]
  /** The decision step this node's screen acts at, so the waiting-count badge
      can narrow its rows the same way that screen does — `filterDecidable`,
      the owner's 2026-08-14 ruling that a user sees only what they may act on.
      Without it the badge counted every row in the status and the queue below
      it showed fewer, with nothing on screen to explain the gap (QA walk,
      2026-08-30). Absent on `archives`, which is a lookup rather than a step
      and carries no badge. */
  step?: DecisionStep
  /** Branch node (Denial Review) renders off the main chain. */
  branch?: boolean
  /** Alternative node (Controller Approval, owner 2026-10-07 — D-303; again
      since 2026-10-09, D-336 amended): drawn DIRECTLY ABOVE the named
      main-path node as a second, parallel branch fed by the same predecessor
      and feeding the same successor. An invoice takes one branch or the
      other, never both. */
  alternativeTo?: NodeKey
  /** A step OFF BY DEFAULT (owner, 2026-10-09 — D-336): since D-350 Manager
      Approval and Oversight Approval, which an invoice visits only when a
      routing rule or an Override requires them (Controller Approval and
      Oversight Approval until then). Drawn with a dotted ring, "to visually
      suggest that they are distinct from" the two steps on the default route. */
  optional?: boolean
  /** Archive node: a terminal-record lookup, not a work step. Renders to the
      right of the branch nodes past its own vertical divider, shows an archive
      glyph instead of a waiting-count badge, and is excluded from the badge
      polling (its queue is unbounded — the screen paginates instead). */
  archive?: boolean
}

export const TIMELINE_NODES: TimelineNode[] = [
  {
    key: 'import-control',
    step: DecisionStep.ImportControl,
    label: 'Import Control',
    role: 'ImportControl',
    queueStatuses: [InvoiceStatus.PendingImportControl],
  },
  {
    // Controller Approval (owner, 2026-10-07 — D-303). On the default route
    // since D-350 (owner, 2026-10-12: "Let's change the default route from
    // Manager Approval to Controller Approval"), so its ring is solid; it was
    // dotted, as a step only a routing rule required, from D-336 until then.
    // Never beside Manager Approval: drawn directly above it as its
    // alternative, both fed from Import Control and both feeding Director
    // Approval (owner, 2026-10-09 — D-336 amended: "we should go back to
    // having Controller Approval sit above Manager Approval as an
    // alternative").
    key: 'controller',
    step: DecisionStep.Controller,
    label: STEP_LABEL[DecisionStep.Controller],
    role: 'ControllerApproval',
    queueStatuses: [InvoiceStatus.PendingController],
    alternativeTo: 'department',
  },
  {
    // Manager Approval (planning §3; renamed from "Department Approval",
    // owner item 8, 2026-08-19, docs/decisions.md D-43 — display only, the
    // stored step/status strings and the `department-head` assignment token
    // are unchanged) — the company's manager. Off the default route since
    // D-350, so required only by a routing rule or an Override; its ring is
    // dotted. An invoice that requires it waits here while nobody is assigned.
    key: 'department',
    step: DecisionStep.Department,
    label: STEP_LABEL[DecisionStep.Department],
    role: 'DepartmentApproval',
    queueStatuses: [InvoiceStatus.PendingDepartment],
    optional: true,
  },
  {
    key: 'director',
    step: DecisionStep.Director,
    label: 'Director Approval',
    role: 'DirectorApproval',
    queueStatuses: [InvoiceStatus.PendingDirector],
  },
  {
    // Required only by a routing rule; its ring is dotted (D-336).
    key: 'oversight',
    step: DecisionStep.Oversight,
    label: 'Oversight Approval',
    role: 'OversightApproval',
    queueStatuses: [InvoiceStatus.PendingOversight],
    optional: true,
  },
  {
    key: 'export-control',
    step: DecisionStep.ExportControl,
    label: 'Export Control',
    role: 'ExportControl',
    queueStatuses: [InvoiceStatus.PendingExportControl],
  },
  {
    // The step-11 checkpoint, split out of Export Control 2026-07-12 (v1.22).
    // Its own team/role (v1.23) — the node stays dark until the user creates
    // "Invoice Processing - Closing Confirmation" and adds members.
    key: 'closing-confirmation',
    step: DecisionStep.ClosingConfirmation,
    label: 'Closing Confirmation',
    role: 'ClosingConfirmation',
    queueStatuses: [InvoiceStatus.PendingExport],
  },
  {
    key: 'denial-review',
    step: DecisionStep.DenialReview,
    label: 'Denial Review',
    role: 'DenialReview',
    queueStatuses: [InvoiceStatus.PendingDenialReview],
    branch: true,
  },
  {
    // Archives (v1.39): read-only lookup of every terminal invoice — the two
    // Point Click Care completions and the six archives, one per Archive
    // Category (D-362; three archived states until 2026-10-14). Not a work
    // step; it sits to the right of Denial Review past a second vertical
    // divider. No count badge (the set is unbounded — the screen paginates), so
    // queueStatuses is documentary here rather than used for a badge query.
    key: 'archives',
    label: 'Archives',
    role: 'Archives',
    queueStatuses: [
      InvoiceStatus.Imported,
      InvoiceStatus.Typed,
      InvoiceStatus.Blank,
      InvoiceStatus.DifferentTypeOfDocument,
      InvoiceStatus.Duplicate,
      InvoiceStatus.PaymentDenied,
      InvoiceStatus.RevisedInvoiceRequested,
      InvoiceStatus.Other,
    ],
    archive: true,
  },
]

/**
 * What a user reads for a STORED `invoices.status` (owner, 2026-11-02 — D-449:
 * "Please make these statuses reflect the Status values inside the Filter's
 * Status picker."; "For the archived ones, show for example, 'Archives -
 * Imported Into Point Click Care'."). An in-progress status reads as its step,
 * the label the picker shows (`Pending Import Control Decision` is *Import
 * Control*); a terminal one reads *Archives - <status>*; `Pending Matching`,
 * which the picker does not offer, reads *Uploading* ("Can we show Pending
 * Matching as just Uploading?"). Built from `TIMELINE_NODES`, so a renamed step
 * cannot drift. DISPLAY only: the stored value, the API and the filters'
 * values are unchanged. Every renderer of a status reads through this —
 * `StatusBadge` foremost — never the stored string (owner item 8, D-43).
 */
const STATUS_DISPLAY = new Map<string, string>([
  [InvoiceStatus.PendingMatching, 'Uploading'],
  ...TIMELINE_NODES.flatMap((node) =>
    node.queueStatuses.map((status): [string, string] => [
      status,
      node.archive === true ? `${node.label} - ${status}` : node.label,
    ]),
  ),
])

export function statusLabel(status: string): string {
  return STATUS_DISPLAY.get(status) ?? status
}

// ---- Internal step roles ----------------------------------------------------
// The API returns roles, not team names: the old client parsed
// "Invoice Processing - Director Approval - <company>" strings to work out what
// a user could do, and that parsing was the fragile part. These are the
// `user_roles` tokens the profile payload carries, mapped to the UI role names.
// A token not listed here maps to nothing and grants nothing (D-293).
//
// `OversightApproval` is deliberately absent: Oversight is not a `user_roles`
// row at all. An active `oversight` appointment IS the whole grant — the tier's
// decision authority and its document access — so the role arrives via
// `assignments`, not here (see identity.ts). It is NOT cross-company: that was
// true only while an oversight row carried a NULL company, which ended
// 2026-08-10; the seat now reaches its own company plus, on the `Other` anchor,
// that anchor's catch-all members, exactly like the other two tiers.
//
export const USER_ROLE_TOKENS = {
  ImportControl: 'import_control',
  ExportControl: 'export_control',
  ClosingConfirmation: 'closing_confirmation',
  DenialReview: 'denial_review',
  Admin: 'admin',
  // The ten per-tab roles (owner item 29, 2026-08-28). Tokens are
  // `<block>_<section>`, byte-identical to `USER_ROLE` in
  // `shared/src/enums.ts` and to the `user_role` enum in
  // `backend/db/schema.sql`.
  ReferencesVendors: 'references_vendors',
  ReferencesCompanies: 'references_companies',
  ReferencesCodes: 'references_codes',
  ReferencesVehicles: 'references_vehicles',
  ReferencesMarketing: 'references_marketing',
  RulesVendors: 'rules_vendors',
  RulesCompanies: 'rules_companies',
  RulesCodes: 'rules_codes',
  RulesGuidance: 'rules_guidance',
  RulesOversight: 'rules_oversight',
  // Rules -> Grouping (owner, 2026-10-22 — D-402).
  RulesGrouping: 'rules_grouping',
} as const satisfies Partial<Record<Role, UserRole>>

export type UserRoleToken = (typeof USER_ROLE_TOKENS)[keyof typeof USER_ROLE_TOKENS]

const ROLE_BY_TOKEN: Record<string, Role> = Object.fromEntries(
  Object.entries(USER_ROLE_TOKENS).map(([role, token]) => [token, role as Role]),
)

/** The UI role for an internal step-role token, or null if unrecognized. */
export function roleForToken(token: string | undefined): Role | null {
  if (!token) return null
  return ROLE_BY_TOKEN[token] ?? null
}

export function tokenForRole(role: Role): UserRoleToken | undefined {
  return (USER_ROLE_TOKENS as Partial<Record<Role, UserRoleToken>>)[role]
}

/** The `user_roles` a person can be granted directly, in `USER_ROLE`'s order:
    the five step roles, one per References or Rules tab, and `Admin`. DERIVED
    from the server's own `USER_ROLES`, so a new tab cannot be forgotten. The
    approval tiers are NOT here — those are appointments in `assignments`. */
export const GRANTABLE_ROLES: readonly Role[] = USER_ROLES.map((token) => roleForToken(token)).filter(
  (role): role is Role => role !== null,
)

// ---- References / Rules sections (owner item 29, 2026-08-28) ---------------
//
// The two Admin blocks that grew into screens of unrelated tabs now carry one
// role PER TAB, the way a company block carries one per approval tier. The
// section key IS the tab value on the screen (`TrainingScreen.tsx`'s
// `EntityTab`, `RulesScreen.tsx`'s `RuleTab`), so the server's answer
// (`capabilities.reference_sections` / `.rules_sections`) and the tab list
// cannot drift.
//
// The section keys and tab labels are `REFERENCE_SECTION*` / `RULES_SECTION*`
// from `@ap/shared`, re-exported above; the maps to the app's `Role` are here.

/** Section -> the UI role that grants it on its own. */
export const REFERENCE_SECTION_ROLE: Record<ReferenceSection, Role> = {
  vendors: 'ReferencesVendors',
  companies: 'ReferencesCompanies',
  codes: 'ReferencesCodes',
  vehicles: 'ReferencesVehicles',
  marketing: 'ReferencesMarketing',
}

export const RULES_SECTION_ROLE: Record<RulesSection, Role> = {
  vendors: 'RulesVendors',
  companies: 'RulesCompanies',
  codes: 'RulesCodes',
  guidance: 'RulesGuidance',
  oversight: 'RulesOversight',
  grouping: 'RulesGrouping',
}


/**
 * The subheader beside the Rules title, PER SUB-TAB (owner, 2026-09-11), the
 * arrangement Analytics already uses. The screen-level sentence it replaced —
 * "Set rules for default routing of invoices." — described only one of the four
 * tabs: Codes maps descriptions to GL codes and Guidance writes instructions
 * for the AI, neither of which is routing.
 *
 * Three of these sentences are not new. They were each tab's own intro
 * paragraph, rendered inside the manager under the tab bar; promoting them to
 * the subheader is a MOVE, and each manager's paragraph went with it so the
 * sentence is not on the page twice. Companies' is new, because that tab holds
 * two managers and neither of the two old paragraphs described both.
 *
 * `SCREEN_SUBHEADER.Rules` still exists and is still the Rules block's help
 * text on Assignments — a block that names the whole screen, which is the one
 * place the general sentence is still the right one.
 */
export const RULES_SECTION_SUBHEADER: Record<RulesSection, string> = {
  vendors: 'Map vendor names as they appear on invoices to vendors, as they are stored in our system.',
  companies:
    'Set intake rules based on the email of the uploading user, and set alternative acceptable aliases for the companies.',
  codes: 'Map vendors and line items to default Accounts, Departments, and Codes.',
  // Rules -> Grouping (owner, 2026-10-22 — D-402).
  grouping: 'Gather a vendor’s line items under named groups, by description or by relevance.',
  // Reworded 2026-09-11 by the manual-authoring work, which landed the same
  // day as this map. Owner item 27 (2026-08-28) wrote the shorter sentence it
  // replaced; this one says what the instructions are FOR.
  guidance:
    'Write instructions to assist with the extraction of information from challenging or unusually formatted invoices.',
  // The fifth tab (owner, 2026-09-15). "criteria" rather than "rules" because
  // every sentence on this screen is already called a rule; what this tab sets
  // is what a rule has to MATCH.
  // Owner, 2026-10-09 (D-336): one sentence "in the spirit of 'By default, an
  // invoice requires Manager Approval and Director Approval to be paid. Here,
  // set the criteria for invoices whose required approvals differ.'" The
  // default route is Controller Approval since D-350.
  oversight:
    'By default, every invoice requires Controller Approval and Director Approval; the rules below set different required approvals for invoices that meet their criteria.',
}

/**
 * The Analytics tab's two sub-tabs (owner, 2026-09-10). Same contract as
 * `REFERENCE_SECTION*` / `RULES_SECTION*` above: the section key IS the tab
 * value in `AnalyticsScreen.tsx`, so the server's answer
 * (`capabilities.analytics_sections`) and the tab list cannot drift.
 *
 * `dashboard` is the content the tab already carried; `reporting` is the
 * criteria-and-PDF screen.
 */
export const ANALYTICS_SECTION_ROLE: Record<AnalyticsSection, Role> = {
  dashboard: 'AnalyticsDashboard',
  reporting: 'AnalyticsReporting',
}

/** The two Analytics seats. */
export const ANALYTICS_ASSIGNMENT_ROLES: readonly string[] = SHARED_ANALYTICS_ASSIGNMENT_ROLES


/** The label on each of the Assignments tab's four screens (owner item ten,
    2026-08-12: "Accounting Assignments" → "Accounting", "Approval
    Assignments" → "Approvals"; the stored tokens `accounting` / `approval` are
    unchanged — this is a label, not data). Keyed by `AssignmentScreen`, so a
    fourth screen is a compile error here rather than a blank tab. */
export const ASSIGNMENT_SCREEN_TITLE: Record<AssignmentScreen, string> = {
  // THE APP'S OWN NAV TABS, one for one and in the same order (owner,
  // 2026-09-14). An admin opening this screen is asking "what should this person
  // be able to use?", and the answer is a tab of the app — so the screens are
  // those tabs rather than the migration-era split by where each role came
  // from, which required knowing that Import Control was "accounting" and
  // Vendors was "data".
  invoices: 'Invoices',
  // Not a nav tab: who receives each notification (owner, 2026-10-07 — D-310).
  notifications: 'Notifications',
  analytics: 'Analytics',
  references: 'References',
  rules: 'Rules',
  assignments: 'Assignments',
}

/** The subheader under a screen's title. Most of these ALSO render as the help
    text on that screen's block in Assignments (owner item twelve, 2026-08-12,
    revised same-day when References/Rules/Analytics moved to the Admin
    screen): ONE literal, TWO renderers — the screen reads it here and
    `ACCOUNTING_STEP_GROUPS` or `ADMIN_STEP_GROUPS` reads it here too, so they
    cannot drift. Two exceptions:
      - `Admin` is deliberately absent: its group describes itself, not a
        screen elsewhere, so it keeps its own literal in `ADMIN_STEP_GROUPS`.
      - `Assignments` has no matching group anywhere — it is the screen that
        HOSTS the Admin/Accounting/Approval groups, not one of them — so it is
        single-rendered, read only by `AssignmentsScreen.tsx` itself.
    A screen with no group at all keeps its own literal too — the three
    approval tiers in `App.tsx`, whose seats all sit on the Approvals listing, which carries no
    help text at all. */
type SubheaderRole =
  | 'ImportControl'
  | 'ExportControl'
  | 'ClosingConfirmation'
  | 'DenialReview'
  | 'References'
  | 'Rules'
  | 'Assignments'

export const SCREEN_SUBHEADER: Record<SubheaderRole, string> = {
  // Owner, 2026-09-29 (D-215, D-225): Import Control's old sentence and AP/AR
  // Approval's, joined, now that the one step does both.
  ImportControl: 'For each invoice, confirm the Company and resolve all outstanding flags.',
  // Owner item 7, 2026-08-19, verbatim — rendered TWICE: this screen's own
  // subheading (`ExportControlScreen.tsx`) and the Export Control blurb on
  // Assignments → Accounting (`ACCOUNTING_STEP_GROUPS` below, rendered by
  // `AssignmentsScreen.tsx`). Both change together on purpose — they describe
  // the same step.
  ExportControl: 'For each Company, generate a file for import to Point Click Care.',
  ClosingConfirmation: 'Confirm that each batch was imported into Point Click Care.',
  // The second half changed with D-312, and again with D-324: a reinstate
  // goes to the first qualifying approval step.
  DenialReview: 'Archive each invoice, or reinstate it to its first qualifying approval step.',
  References: 'Add and edit the master records that drive matching and dropdown options, and make them Active or Inactive.',
  Rules: 'Set rules for default routing of invoices.',
  // Owner item 12, 2026-08-13.
  Assignments: 'Assign staff to roles to grant permissions.',
  // NO `Analytics` (owner, 2026-11-04 — D-469: "Drop 'See where the queue
  // stands right now.'"). Its only reader was the Dashboard tab's intro line.
}

/**
 * The subheader beside the Analytics title, PER SUB-TAB (owner, 2026-09-10).
 *
 * The title and subheader sit above the tab bar, the way References and Rules
 * do it, and the subheader swaps with the selected tab: the two sentences
 * describe different jobs, so one sentence over both would misdescribe whichever
 * tab was open.
 *
 * Both tabs' lines are dropped now (owner, 2026-11-04): Reporting's first, then
 * the Dashboard's, which was `SCREEN_SUBHEADER.Analytics` (D-469). The map stays
 * so a tab can carry a line again without touching `AnalyticsScreen.tsx`.
 */
export const ANALYTICS_SECTION_SUBHEADER: Partial<Record<AnalyticsSection, string>> = {
  // The Dashboard has none (owner, 2026-11-04 — D-469: "Drop 'See where the
  // queue stands right now.'").
  // Reporting has none (owner, 2026-11-04: "Drop 'Set your filters, and export
  // collections of relevant invoices.'").
}

/** NO `blurb` (owner, 2026-09-14: "in Assignments, let's drop the help text
    under each role"). Each spec carried its screen's own `SCREEN_SUBHEADER`
    line, rendered under the group title on Assignments; the screen stopped
    rendering it, and a duplicate of a string that lives one map away is not
    worth keeping for its own sake. `SCREEN_SUBHEADER[spec.role]` is still the
    answer wherever a subheader is wanted. */
export interface StepGroupSpec {
  role: Role
  title: string
}

// The Assignments tab is now three screens — Admin, Accounting, Approvals —
// each reading top to bottom in a fixed order. Ordering lives here rather
// than inside the screen so it is real data a test can read, not a shape
// inferred from source text.
//
// THE SCREEN FILE IS `TrainingScreen.tsx` and its nav key is `training`: the
// References screen kept both from its first name. Every other name for it —
// the block, the role prefix, the title — is References.

/** The Admin screen. Admin itself, and nothing else since 2026-09-03 (owner:
    "move References, Rules, and Analytics out of the Admin sub-tab and into a
    new one, called Data"). The three that left are `DATA_STEP_GROUPS` below.

    `visibleAssignmentScreens` gates every Assignments screen on literal
    `hasRole(session,'Admin')` (admin-only since 2026-10-07) — DELIBERATELY NOT
    derived from this array, because deriving it would let a role-holder onto
    the screen that appoints their own role, which is the escalation surface
    this project explicitly refuses (see that function and
    `docs/security.md`). */
export const ADMIN_STEP_GROUPS: readonly StepGroupSpec[] = [
  { role: 'Admin', title: 'Admin' },
]

/** The Data screen (owner, 2026-09-03): the three roles that govern master
    data, routing rules and cross-cutting metrics rather than a pipeline step.
    They sat on the Admin screen from owner item 12 (2026-08-13) until this
    date — the reason they moved there still holds (appointing who may see or
    change any of the three is the same KIND of decision as appointing Admin,
    not an accounting-floor one), and the owner asked only that they stop
    sharing a tab with Admin itself.

    **STILL ADMIN-ONLY.** `visibleAssignmentScreens` gates this screen on the
    same literal `hasRole(session,'Admin')` as the Admin screen, and that is
    the whole point of splitting the LABEL and not the GATE: deriving the gate
    from this array — the obvious-looking thing to do for a tab named after
    its contents — would let a References-only holder onto the screen that
    appoints References. Accepted consequence, unchanged from item 12 and
    still recorded in `fix-assignment-screens.test.ts`: a References/Rules/
    Analytics-only holder sees no group of their own anywhere on the
    Assignments tab, and still reaches their own screen directly, because each
    of those three is gated on holding the role rather than on Assignments. */
export const DATA_STEP_GROUPS: readonly StepGroupSpec[] = [
  { role: 'References', title: 'References' },
  { role: 'Rules', title: 'Rules' },
  { role: 'Analytics', title: 'Analytics' },
]

/** The Accounting screen: the four pipeline step roles (Manual Entry was a
    fifth until the step was dropped, D-311). References, Rules and
    Analytics moved out on owner item 12 (2026-08-13) — none of the three is an
    accounting-floor decision — first to `ADMIN_STEP_GROUPS`, and since
    2026-09-03 to `DATA_STEP_GROUPS`. */
export const ACCOUNTING_STEP_GROUPS: readonly (StepGroupSpec & { role: SubheaderRole })[] = [
  { role: 'ImportControl', title: 'Import Control' },
  { role: 'ExportControl', title: 'Export Control' },
  { role: 'ClosingConfirmation', title: 'Closing Confirmation' },
  { role: 'DenialReview', title: 'Denial Review' },
]
